import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type BasicTeammate,
} from "@repo/app-types/entities/teammate";
import {
  getGradingDisplayNames,
  type TeammateAccountGrading,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { ShortAbsoluteTimestamp } from "@repo/ui/timestamps/shortAbsoluteTimestamp";
import { IconExternalLink } from "@tabler/icons-react";
import type { FC } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { CriterionAverageScore } from "./criterionAverageScore.js";
import { renderGapBarWithTooltip } from "./gapBar.js";
import { renderGradingValue } from "./gradingHelpers.js";

export interface CriteriaScoreCardProps {
  grading: TeammateAccountGrading;
  criterion: PlaybookCriterionNode;
  teammate: BasicTeammate;
  teammateAverageScore: number;
  medianAverageScore?: number;
  to: string;
}

export const CriteriaScoreCardSkeleton: FC = () => {
  return (
    <div
      className="w-[260px] shrink-0 rounded-md bg-white p-2.5 shadow-sm ring-1 ring-black/5"
      role="status"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className={skeletonStyle({ className: "h-5 w-24" })} />
        </div>
        <div className={skeletonStyle({ className: "h-5 w-32" })} />
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

export const CriteriaScoreCard: FC<CriteriaScoreCardProps> = ({
  grading,
  criterion,
  teammate,
  teammateAverageScore,
  medianAverageScore,
  to,
}) => {
  const advisorName = buildTeammateDisplayNames(teammate).primaryDisplayName;
  const customerName =
    getGradingDisplayNames(grading).primaryDisplayName ?? grading.name;
  const score = renderGradingValue(
    criterion.grading_value_type,
    grading.assessment?.criteria_grading[criterion.id]?.value,
    { showLabel: false },
  );
  const isYesNo = criterion.grading_value_type === "yes_no";
  const scoreValue = isYesNo
    ? Math.round(teammateAverageScore * 100)
    : parseFloat(teammateAverageScore.toFixed(1));
  const benchmarkValue =
    medianAverageScore !== undefined
      ? isYesNo
        ? Math.round(medianAverageScore * 100)
        : parseFloat(medianAverageScore.toFixed(1))
      : undefined;

  return (
    <Link
      className="group relative block w-[260px] shrink-0 rounded-md bg-white p-2.5 shadow-sm ring-1 ring-black/5 transition hover:bg-slate-50"
      to={to}
      target="_blank"
      rel="noreferrer"
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <div
            className="min-w-0 truncate text-sm font-medium text-foreground"
            title={advisorName}
          >
            {advisorName}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <CriterionAverageScore
              valueType={criterion.grading_value_type}
              value={teammateAverageScore}
            />
            {benchmarkValue !== undefined
              ? renderGapBarWithTooltip(
                  isYesNo ? `${scoreValue}%` : `${scoreValue}`,
                  scoreValue,
                  benchmarkValue,
                  isYesNo ? undefined : { range: [1, 5] },
                )
              : null}
          </div>
        </div>
        <div className="mt-1 flex items-center gap-x-1.5 border-t border-slate-100 pt-2">
          <div className="shrink-0">{score}</div>
          <div
            className={twMerge(
              "min-w-0 truncate text-xs text-foreground",
              PH_MASK_CLASS,
            )}
            title={customerName || "--"}
          >
            {customerName || "--"}
          </div>
          <span className="shrink-0 text-xs text-slate-400">&middot;</span>
          <div className="shrink-0 text-xs text-muted-foreground">
            <ShortAbsoluteTimestamp timestamp={grading.last_recorded_at} />
          </div>
        </div>
      </div>
      <IconExternalLink className="pointer-events-none absolute right-2 bottom-3 size-4 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
};
